import { useCommunityIsIcp } from "@circle-react/components/CommunityOnboarding/NewChecklistComponents/CircleAppLinks/useCommunityIsIcp";
import { supportedCurrencies } from "@circle-react/config/Currencies";
import { planIntervals } from "@circle-react/helpers/plansHelpers";
import type { PlanIntervals } from "@circle-react/helpers/plansHelpers";
import { useAccountSubscriptionApi } from "@circle-react/hooks/useAccountSubscriptionApi";
import { usePlanStatus } from "@circle-react/hooks/usePlanStatus";
import type {
  CustomPlanOffer,
  Plan,
  PlanFeatureFlags,
} from "@circle-react/types";
import type { CurrentPlan, CustomPlanPricing } from "@circle-react/types";
import { useSearch } from "@tanstack/react-router";
import type { ReactNode } from "react";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

import { usePunditUserContext } from "@/react/contexts/punditUserContext";
import type { PlanTiers } from "@/react/helpers/planHelpers";
import { planTiers } from "@/react/helpers/planHelpers";
import { useScheduleDowngradesApi } from "@/react/hooks/useScheduleDowngradesApi";

import { useCurrentPlanApi } from "../hooks/useCurrentPlanApi";
import { useFetchPlans } from "../hooks/usePlansApi";
import { getDefaultPlanTier } from "../utils/getDefaultPlanTier";

interface PlanContextProps {
  billingCurrency: string | null;
  billingCycle: PlanIntervals | null;
  compareTablePlanFeatures?: Record<string, PlanFeatureFlags>;
  currentPlan?: CurrentPlan;
  customPlanOffer?: CustomPlanOffer;
  customPlanOfferPlan?: CustomPlanPricing;
  isCurrentPlanLoading: boolean;
  isListingPlans: boolean;
  isPlansLoading: boolean;
  isRenewLoading: boolean;
  isSchedulingDowngrade: boolean;
  isUpdatingSubscription: boolean;
  plans: Plan[];
  refetchCurrentPlan: any;
  refetchPlans: any;
  renewSubscription: any;
  scheduleDowngrade: ReturnType<
    typeof useScheduleDowngradesApi
  >["scheduleDowngrade"];
  selectedPlan?: Plan;
  selectedPlanTier?: PlanTiers;
  setBillingCycle: (billingCycle: PlanIntervals) => void;
  setSelectedPlanTier: (planTier: PlanTiers) => void;
  shouldRenderBillingCurrency: boolean;
  source?: string;
  tierDescriptions?: Partial<Record<PlanTiers, string>>;
  updateSubscription: any;
}

const PlansContext = createContext<PlanContextProps | undefined>(undefined);
PlansContext.displayName = "PlansContext";

export const usePlans = (): PlanContextProps => {
  const context = useContext(PlansContext);
  if (!context) {
    throw new Error("usePlans must be used within a PlansProvider");
  }
  return context;
};

interface PlansProviderProps {
  children: ReactNode;
  source?: string;
  tierDescriptions?: Partial<Record<PlanTiers, string>>;
}

export const PlansProvider = ({
  children,
  source,
  tierDescriptions,
}: PlansProviderProps) => {
  const {
    billing_cycle: queryBillingCycle,
    billing_currency: queryBillingCurrency,
    locale: queryLocale,
  } = useSearch({ strict: false }) ?? {};

  const { currentPlan, isCurrentPlanLoading, refetchCurrentPlan } =
    useCurrentPlanApi();
  const {
    isCommunityOnPaidPlan,
    isCommunityOnTrial,
    isExpiredTrial,
    canChooseCurrency,
  } = usePlanStatus();
  const isICP = useCommunityIsIcp();
  const { currentCommunity } = usePunditUserContext();
  const hasActiveCommunityCoupon =
    !!currentCommunity?.has_active_community_coupon;

  const getDefaultBillingCycle = useCallback(() => {
    if (isCommunityOnTrial || isExpiredTrial) {
      return planIntervals.year;
    }

    if (!isCurrentPlanLoading && currentPlan) {
      return currentPlan.interval;
    }

    return planIntervals.year;
  }, [isCommunityOnTrial, isCurrentPlanLoading, currentPlan, isExpiredTrial]);

  const [billingCycle, setBillingCycle] = useState<PlanIntervals | null>(null);
  const [locale, setLocale] = useState<string | null>(null);
  const [billingCurrency, setBillingCurrency] = useState<string | null>(null);

  const {
    renewSubscription,
    isRenewLoading,
    updateSubscription,
    isUpdatingSubscription,
  } = useAccountSubscriptionApi();

  const { scheduleDowngrade, isSchedulingDowngrade } =
    useScheduleDowngradesApi();

  const {
    plans,
    compareTablePlanFeatures,
    customPlanOffer,
    customPlanOfferPlan,
    isPlansLoading,
    refetchPlans,
  } = useFetchPlans({
    billing_cycle: billingCycle,
    billing_currency: billingCurrency,
    locale: locale,
  });

  useEffect(() => {
    if (queryBillingCurrency) {
      setBillingCurrency(queryBillingCurrency);
    } else if (currentPlan) {
      setBillingCurrency(null);
    }

    if (queryLocale) {
      setLocale(queryLocale);
    }

    if (
      queryBillingCycle &&
      (queryBillingCycle === planIntervals.month ||
        queryBillingCycle === planIntervals.year)
    ) {
      setBillingCycle(queryBillingCycle);
    } else if (currentPlan) {
      setBillingCycle(getDefaultBillingCycle());
    }
  }, [
    queryBillingCycle,
    queryBillingCurrency,
    queryLocale,
    getDefaultBillingCycle,
    currentPlan,
  ]);

  const isListingPlans = isPlansLoading || isCurrentPlanLoading;

  const hasLoadedPlans = plans.length > 0 && !isPlansLoading;
  const firstPlanCurrency = hasLoadedPlans ? plans[0].currency : null;

  const shouldRenderBillingCurrency =
    canChooseCurrency &&
    (!!queryBillingCurrency ||
      firstPlanCurrency === supportedCurrencies.BRL.code.toLowerCase());

  const billingCurrencyToDisplay = firstPlanCurrency || billingCurrency;

  const [selectedPlanTier, setSelectedPlanTier] = useState<
    PlanTiers | undefined
  >(undefined);

  const defaultPlanTier = useMemo(
    () =>
      getDefaultPlanTier({
        plans,
        currentPlan,
        isCommunityOnTrial,
        customPlanOfferPlan,
        isExpiredTrial,
        isCommunityOnPaidPlan,
        isICP,
        hasActiveCommunityCoupon,
      }),
    [
      plans,
      currentPlan,
      customPlanOfferPlan,
      isCommunityOnTrial,
      isExpiredTrial,
      isCommunityOnPaidPlan,
      isICP,
      hasActiveCommunityCoupon,
    ],
  );

  useEffect(() => {
    if (selectedPlanTier === undefined && defaultPlanTier) {
      setSelectedPlanTier(defaultPlanTier);
    }
  }, [selectedPlanTier, defaultPlanTier]);

  const selectedPlan = useMemo(() => {
    if (selectedPlanTier === planTiers.plus && customPlanOfferPlan) {
      // CustomPlanPricing includes all fields consumed by SubmitButton and PlanSummary components.
      // Missing Plan fields (e.g. plan_feature_flags) are not accessed on this code path.
      return customPlanOfferPlan as unknown as Plan;
    }
    return plans.find(plan => plan.tier === selectedPlanTier);
  }, [selectedPlanTier, plans, customPlanOfferPlan]);

  return (
    <PlansContext.Provider
      value={{
        plans,
        isPlansLoading,
        selectedPlanTier,
        selectedPlan,
        currentPlan,
        isCurrentPlanLoading,
        billingCycle,
        billingCurrency: billingCurrencyToDisplay,
        renewSubscription,
        isRenewLoading,
        shouldRenderBillingCurrency,
        tierDescriptions,
        updateSubscription,
        isUpdatingSubscription,
        refetchPlans,
        refetchCurrentPlan,
        isListingPlans,
        setSelectedPlanTier,
        setBillingCycle,
        scheduleDowngrade,
        isSchedulingDowngrade,
        compareTablePlanFeatures,
        customPlanOffer,
        customPlanOfferPlan,
        source,
      }}
    >
      {children}
    </PlansContext.Provider>
  );
};
